import React, {useCallback, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {AnimatePresence, motion} from 'motion/react';
import clsx from 'clsx';
import {goWhatsAppChat} from '@/utils';
import {useShare} from '../hooks/share.hooks';
import {getRecordList} from './invitation.service';
import {RecordListItem} from './invitation.type';
import {defaultHeaderImg, whatsappIcon} from './invitation.variables';
import {ProxyH5Header, useInfinitePager} from '../proxy/proxy-h5.shared';

const emptyImage = require('@components/assets/imgs/empty.webp');

const quickFilterOptions = [
  {value: '0', labelKey: 'filter.today'},
  {value: '1', labelKey: 'filter.yesterday'},
  {value: '7', label: '7 Days'},
  {value: '15', label: '15 Days'},
  {value: '30', label: '30 Days'},
];

const formatShortDate = (startDate?: number, endDate?: number) => {
  if (!startDate || !endDate) {
    return 'All';
  }
  if (dayjs(startDate).isSame(endDate, 'day')) {
    return dayjs(startDate).format('DD/MM');
  }
  return `${dayjs(startDate).format('DD/MM')}-${dayjs(endDate).format(
    'DD/MM',
  )}`;
};

const getCalendarDays = (monthDate: dayjs.Dayjs) => {
  const startOfMonth = monthDate.startOf('month');
  const daysInMonth = monthDate.daysInMonth();
  const startOffset = startOfMonth.day();
  const cellCount = Math.ceil((startOffset + daysInMonth) / 7) * 7;
  const firstCell = startOfMonth.subtract(startOffset, 'day');
  return Array(cellCount)
    .fill(0)
    .map((_, index) => firstCell.add(index, 'day'));
};

const FilterIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" className="h-3.5 w-3.5">
    <path
      d="M2 3h10L8.2 7.3V11L5.8 12V7.3L2 3Z"
      stroke="#5F6975"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
    <rect x="2.5" y="3.5" width="11" height="10" rx="1" stroke="#5F6975" />
    <path d="M2.5 6.5H13.5" stroke="#5F6975" />
    <path d="M5 2.5V4.5M11 2.5V4.5" stroke="#5F6975" strokeLinecap="round" />
  </svg>
);

const DownIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3">
    <path
      d="M3 4.5 6 7.5 9 4.5"
      stroke="#9FA5AC"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    fill="none"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6">
    <path
      d="M16.8002 7.2002L7.2002 16.8002"
      stroke="#848B94"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.2002 7.2002L16.8002 16.8002"
      stroke="#848B94"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SortArrow = ({sort}: {sort: 'asc' | 'desc'}) => (
  <span className="ml-1 flex flex-col gap-[1px] leading-none">
    <svg viewBox="0 0 8 5" className="h-1.5 w-2">
      <path d="M4 0 8 5H0L4 0Z" fill={sort === 'asc' ? '#E45E5E' : '#C8CDD4'} />
    </svg>
    <svg viewBox="0 0 8 5" className="h-1.5 w-2">
      <path
        d="M4 5 0 0h8L4 5Z"
        fill={sort === 'desc' ? '#E45E5E' : '#C8CDD4'}
      />
    </svg>
  </span>
);

const sheetMotionTransition = {
  duration: 0.24,
  ease: [0.2, 0.8, 0.2, 1] as [number, number, number, number],
};

const sheetMotionProps = {
  initial: {
    opacity: 0,
    y: 80,
    scale: typeof window !== 'undefined' && window.innerWidth >= 640 ? 1.03 : 1,
  },
  animate: {opacity: 1, y: 0, scale: 1},
  exit: {
    opacity: 0,
    y: 80,
    scale: typeof window !== 'undefined' && window.innerWidth >= 640 ? 1.03 : 1,
  },
  transition: sheetMotionTransition,
};

function QuickFilterSheet({
  open,
  title,
  options,
  onClose,
  onSelect,
}: {
  open: boolean;
  title: string;
  options: {value: string; label: string}[];
  onClose: () => void;
  onSelect: (value: string) => void;
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="quick-filter-sheet"
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          transition={{duration: 0.2, ease: 'easeOut'}}
          className="fixed inset-0 z-50 flex h-[100dvh] w-screen items-end justify-center overflow-x-auto"
          onClick={onClose}>
          <motion.section
            role="dialog"
            tabIndex={-1}
            aria-modal="true"
            {...sheetMotionProps}
            className={clsx(
              'relative z-50 m-0 flex w-full max-w-md flex-col overflow-y-hidden bg-white',
              'rounded-t-lg rounded-none shadow-small',
            )}
            onClick={event => event.stopPropagation()}>
            <div className="flex flex-row items-center justify-between p-5">
              <div className="text-sm font-bold text-[#31373D]">{title}</div>
              <button
                type="button"
                className="border-0 bg-transparent p-0"
                onClick={onClose}>
                <CloseIcon />
              </button>
            </div>
            <div className="flex flex-col px-5 pb-3">
              {options.map(item => (
                <button
                  type="button"
                  key={item.value}
                  className="flex h-12 w-full appearance-none flex-row items-center justify-between border-0 bg-[var(--basic-color-white)] p-3 text-left shadow-none"
                  onClick={() => {
                    onSelect(item.value);
                    onClose();
                  }}>
                  <div className="text-sec text-sm">{item.label}</div>
                </button>
              ))}
            </div>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DatePickerSheet({
  open,
  startValue,
  endValue,
  onClose,
  onSelect,
}: {
  open: boolean;
  startValue?: number;
  endValue?: number;
  onClose: () => void;
  onSelect: (startDate: number, endDate: number) => void;
}) {
  const [selectingStart, setSelectingStart] = useState(true);
  const [selectedStartDate, setSelectedStartDate] = useState(
    dayjs(startValue || Date.now()),
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    dayjs(endValue || startValue || Date.now()),
  );
  const [monthDate, setMonthDate] = useState(
    dayjs(startValue || Date.now()).startOf('month'),
  );

  React.useEffect(() => {
    if (open) {
      const now = Date.now();
      const nextStartDate = dayjs(startValue || now);
      const nextEndDate = dayjs(endValue || startValue || now);
      setSelectingStart(true);
      setSelectedStartDate(nextStartDate);
      setSelectedEndDate(nextEndDate);
      setMonthDate(nextStartDate.startOf('month'));
    }
  }, [endValue, open, startValue]);

  if (!open) {
    return null;
  }

  const calendarDays = getCalendarDays(monthDate);
  const activeDate = selectingStart ? selectedStartDate : selectedEndDate;

  return (
    <div className="fixed inset-0 z-50 bg-black/45">
      <div className="mx-auto min-h-full w-full max-w-[var(--max-screen-width)] bg-white px-5 pt-6">
        <div className="mb-5 flex items-center justify-between">
          <div className="text-sm font-bold text-[var(--proxy-main)]">
            Select a time
          </div>
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 text-xl font-bold leading-none text-[var(--proxy-second)]"
            onClick={onClose}>
            ×
          </button>
        </div>
        <div className="mb-1 text-sm font-medium text-[var(--proxy-second)]">
          {selectingStart ? 'Start:' : 'End:'}
        </div>
        <div className="overflow-hidden rounded border border-[#9FA5AC]">
          <div className="relative flex h-16 items-center justify-center bg-[#f5f5f5]">
            <button
              type="button"
              className="absolute left-4 flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 text-2xl text-[#c9c9c9]"
              onClick={() => setMonthDate(prev => prev.subtract(1, 'month'))}>
              ‹
            </button>
            <div className="text-base font-bold text-black">
              {monthDate.format('MMMM YYYY')}
            </div>
          </div>
          <div className="grid h-10 grid-cols-7 items-center border-t border-[#c9c9c9] bg-[#f5f5f5] text-center text-sm font-bold text-black">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(item => (
              <div key={item}>{item}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 bg-white pb-3 pt-2">
            {calendarDays.map(date => {
              const isCurrentMonth = date.month() === monthDate.month();
              const isSelected = date.isSame(activeDate, 'day');
              const isBeforeStart =
                !selectingStart && date.isBefore(selectedStartDate, 'day');
              return (
                <button
                  type="button"
                  key={date.format('YYYY-MM-DD')}
                  disabled={isBeforeStart}
                  className={`mx-auto my-1 flex h-7 w-7 items-center justify-center rounded border-0 p-0 text-sm font-medium ${
                    isSelected
                      ? 'bg-[#bde9f5] text-[var(--proxy-main)]'
                      : isBeforeStart
                      ? 'bg-transparent text-[#d9dcdf]'
                      : isCurrentMonth
                      ? 'bg-transparent text-black'
                      : 'bg-transparent text-[#bfc3c7]'
                  }`}
                  onClick={() => {
                    if (selectingStart) {
                      setSelectedStartDate(date);
                      setSelectedEndDate(date);
                      setMonthDate(date.startOf('month'));
                      setSelectingStart(false);
                      return;
                    }
                    setSelectedEndDate(date);
                    onSelect(selectedStartDate.valueOf(), date.valueOf());
                    onClose();
                  }}>
                  {date.date()}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

type InvitationRecordWebItem = RecordListItem;

const InvitationRecordWeb = () => {
  const {t} = useTranslation();
  const {doLegacyInviteShare} = useShare(true);
  const [sort, setSort] = useState<'asc' | 'desc'>('desc');
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [timeFilterValue, setTimeFilterValue] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [query, setQuery] = useState({
    startTime: '',
    endTime: '',
    sort: 'desc' as 'asc' | 'desc',
  });

  const request = useCallback(
    (pageNo: number, pageSize: number) =>
      getRecordList({
        pageNo,
        pageSize,
        ...query,
      }),
    [query],
  );

  const {items, loading, loadingMore, isEnd, sentinelRef} =
    useInfinitePager<InvitationRecordWebItem>(request);

  const sortedItems = useMemo(() => {
    return items;
  }, [items]);

  const quickFilterLabels = useMemo(
    () =>
      quickFilterOptions.map(item => ({
        value: item.value,
        label: item.label || t(item.labelKey || ''),
      })),
    [t],
  );

  const dateLabel = useMemo(() => {
    if (timeFilterValue) {
      const current = quickFilterLabels.find(
        item => item.value === timeFilterValue,
      );
      return current?.label || t('label.all');
    }
    return formatShortDate(
      startDate ? dayjs(startDate).valueOf() : undefined,
      endDate ? dayjs(endDate).valueOf() : undefined,
    );
  }, [endDate, quickFilterLabels, startDate, t, timeFilterValue]);

  const applyQuickFilter = useCallback((value: string) => {
    const nextStart = dayjs().subtract(+value, 'day');
    const nextEnd = dayjs().subtract(+value > 0 ? 1 : 0, 'day');
    setTimeFilterValue(value);
    setStartDate(nextStart.format('YYYY-MM-DD'));
    setEndDate(nextEnd.format('YYYY-MM-DD'));
  }, []);

  const applyDateRange = useCallback((nextStart: number, nextEnd: number) => {
    setTimeFilterValue('');
    setStartDate(dayjs(nextStart).format('YYYY-MM-DD'));
    setEndDate(dayjs(nextEnd).format('YYYY-MM-DD'));
  }, []);

  const handleInvite = useCallback(() => {
    doLegacyInviteShare();
  }, [doLegacyInviteShare]);

  React.useEffect(() => {
    setQuery(current => ({
      ...current,
      startTime: startDate ? dayjs(startDate).format('YYYY-MM-DD') : '',
      endTime: endDate ? dayjs(endDate).format('YYYY-MM-DD') : '',
      sort,
    }));
  }, [endDate, sort, startDate]);

  const sheetOpen = filterSheetOpen || datePickerOpen;

  return (
    <div className="proxy-h5-page relative flex min-h-full flex-col bg-[#EFF2F7]">
      <AnimatePresence>
        {sheetOpen && (
          <motion.div
            key="invitation-record-overlay"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.18, ease: 'easeOut'}}
            className="pointer-events-none absolute inset-0 z-20 bg-[rgba(94,63,73,0.26)]"
          />
        )}
      </AnimatePresence>
      <div className="relative z-10">
        <ProxyH5Header
          title={t('invitation-record.invitation-record')}
          className="h-[3.5rem] border-b border-[rgba(31,35,41,0.08)] shadow-none"
          titleClassName="pt-[0.0625rem] text-[1rem] font-bold leading-none text-[#1F2329]"
          leftClassName="pl-[0.5625rem]"
          rightClassName="pr-[0.6875rem]"
          backButtonClassName="mr-0 h-7 w-7"
        />
      </div>
      <div className="relative flex-1 overflow-y-auto">
        <div className="absolute left-0 top-0 h-[18.3125rem] w-full bg-[linear-gradient(180deg,#FBCBCB_0%,#EFF2F7_100%)]" />
        <div className="relative z-10 px-3 pt-[0.6875rem]">
          <div className="flex items-center justify-between gap-1.5">
            <button
              type="button"
              className="flex h-7 min-w-[5.5rem] items-center rounded-[4px] border border-[#E8EAEE] bg-white px-3 text-xs text-[#31373D]"
              onClick={() =>
                setSort(current => (current === 'desc' ? 'asc' : 'desc'))
              }>
              <span>{t('proxy.filter.recharge')}</span>
              <SortArrow sort={sort} />
            </button>
            <div className="ml-auto flex items-center gap-1.5">
              <button
                type="button"
                className="flex h-7 min-w-[5.125rem] items-center gap-1 rounded-[4px] border border-[#E8EAEE] bg-white px-3 text-xs text-[#5F6975]"
                onClick={() => setFilterSheetOpen(true)}>
                <FilterIcon />
                <span>{t('filter.name')}</span>
                <DownIcon />
              </button>
              <button
                type="button"
                className="flex h-7 min-w-[5.75rem] items-center gap-1 rounded-[4px] border border-[#E8EAEE] bg-white px-3 text-xs text-[#5F6975]"
                onClick={() => setDatePickerOpen(true)}>
                <CalendarIcon />
                <span>{dateLabel}</span>
                <DownIcon />
              </button>
            </div>
          </div>

          {sortedItems.length === 0 && !loading ? (
            <div className="flex min-h-[calc(100dvh-6.25rem)] flex-col">
              <div className="flex-1" />
              <div className="flex flex-col items-center px-6">
                <img
                  src={emptyImage}
                  alt=""
                  className="h-[8.125rem] w-[8.125rem]"
                />
                <div className="mt-[1.75rem] text-center text-sm font-medium text-black">
                  {t('invitation-record.invited-friends')}
                </div>
                <button
                  type="button"
                  className="mt-4 h-10 min-w-[10.875rem] rounded-full border-0 bg-[var(--basic-color-primary)] px-8 text-base font-bold text-white"
                  onClick={handleInvite}>
                  {t('invitation-record.invite')}
                </button>
                <div className="mt-3 text-center text-sm font-medium text-black">
                  {t('label.noMoreData')}
                </div>
              </div>
              <div className="flex-[2]" />
            </div>
          ) : (
            <div className="pb-4 pt-3">
              {sortedItems.map((item, index) => (
                <div
                  key={`${
                    item.inviteUserId || item.inviteTaskUserDetailId
                  }-${index}`}
                  className="mb-3 rounded-[8px] bg-white p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <img
                        src={item.userAvatar || defaultHeaderImg}
                        alt=""
                        className="h-6 w-6 rounded-full"
                      />
                      <span className="ml-2 text-xs font-bold text-black">
                        {item.inviteUserPhone || '-'}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0"
                      onClick={() =>
                        goWhatsAppChat(item.inviteUserPhone || '')
                      }>
                      <img
                        src={whatsappIcon}
                        alt="whatsapp"
                        className="h-6 w-6"
                      />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-[var(--font-color-second)]">
                      {t('invitation-record.status')}
                    </span>
                    <span
                      className={clsx(
                        'font-bold',
                        item.status === 0
                          ? 'text-[var(--basic-color-primary)]'
                          : 'text-black',
                      )}>
                      {item.status === 0 ? 'Incomplete' : 'Completed'}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-[var(--font-color-second)]">
                      {t('invitation-record.registration-time')}
                    </span>
                    <span className="text-black">
                      {item.createTime
                        ? dayjs(item.createTime).format('YYYY-MM-DD HH:mm:ss A')
                        : '-'}
                    </span>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="py-3 text-center text-sm text-[#848B94]">
                  Loading...
                </div>
              )}
              <div ref={sentinelRef} />
              {loadingMore && (
                <div className="py-3 text-center text-sm text-[#848B94]">
                  Loading...
                </div>
              )}
              {!loading && !loadingMore && isEnd && sortedItems.length > 0 && (
                <div className="py-3 text-center text-sm text-[var(--font-color-second)]">
                  {t('label.noMoreData')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <QuickFilterSheet
        open={filterSheetOpen}
        title="Select a time"
        options={quickFilterLabels}
        onClose={() => setFilterSheetOpen(false)}
        onSelect={applyQuickFilter}
      />
      <DatePickerSheet
        open={datePickerOpen}
        startValue={startDate ? dayjs(startDate).valueOf() : undefined}
        endValue={endDate ? dayjs(endDate).valueOf() : undefined}
        onClose={() => setDatePickerOpen(false)}
        onSelect={applyDateRange}
      />
    </div>
  );
};

export default InvitationRecordWeb;
